import { CartLineItem } from '@portal/api/shopifyV2/types';
import { isOTPWelcomeKit } from '@portal/shared/utils/shopify/isOTPWelcomeKit';
import { isSubscriptionWelcomeKit } from '@portal/shared/utils/shopify/isSubscriptionWelcomeKit';
import { isAgzFrotherWelcomeKit } from '@portal/shared/utils/shopify/isAgzFrotherWelcomeKit';
import { getCartRulesMetaobjects } from '../../api/shopifyV2/queries/cartRulesMetaobjects';
import { shopifyFetch } from '../../api/shopifyV2/utils';
import { SHOPIFY_STORE_DOMAIN, SHOPIFY_STOREFRONT_API_TOKEN } from '../../config/base';
import qaCartRules from '../../config/cart-rules/qa.json';
import usCartRules from '../../config/cart-rules/us.json';
import euCartRules from '../../config/cart-rules/eu.json';
import ukCartRules from '../../config/cart-rules/uk.json';
import { StoreCode, STORES_QA } from '../../schema/storeCode';

// Default quantity limits for cart items
export const DEFAULT_MAX_QUANTITY_ONE_TIME = 6;
export const DEFAULT_MAX_QUANTITY_SUBSCRIPTION = 3;

export interface CartRule {
  min_quantity?: number;
  max_quantity?: number;
  when_variants: string[];
  then_variants: string[];
  rule_type: string;
  group_id?: string;
  bidirectional?: boolean;
  requires_any_of?: string[];
  requires_none_of?: string[];
  requires_customer_tags?: string[];
  requires_no_customer_tags?: string[];
}

/**
 * Get cart rules configuration for a store
 * Returns an array of all cart rules for the store
 */
function getCartRulesForStore(storeCode: StoreCode): CartRule[] {
  // Use us.json for US store
  if (storeCode === 'us') {
    return usCartRules as CartRule[];
  }

  // Use qa.json for QA stores
  if (STORES_QA.includes(storeCode)) {
    return qaCartRules as CartRule[];
  }

  // Use eu.json for EU store
  if (storeCode === 'eu') {
    return euCartRules as CartRule[];
  }

  // Use uk.json for UK store
  if (storeCode === 'uk') {
    return ukCartRules as CartRule[];
  }

  // Return empty rules for stores without specific configuration
  return [];
}

/**
 * Get the appropriate custom attribute key for welcome kit products
 */
function getWelcomeKitAttributeKey(targetId: string, storeCode: StoreCode) {
  if (isOTPWelcomeKit(targetId, storeCode)) {
    return '__custom.otp_welcome_kit';
  }

  if (isAgzFrotherWelcomeKit(targetId, storeCode)) {
    return '__custom.agz_frother_welcome_kit';
  }

  return '__custom.welcome_kit';
}

/**
 * Check if a store is using QA/dev cart rules
 */
export function isStoreUsingQACartRules(storeCode: StoreCode): boolean {
  return STORES_QA.includes(storeCode);
}

// Development flag - set to false to use real API
const USE_LOCAL_DATA = true;

/**
 * Helper function to get cart rules count for a store
 */
export function getCartRulesCount(storeCode: StoreCode): number {
  return getCartRulesForStore(storeCode).length;
}

/**
 * Cart rules processor
 */
export class CartRules {
  private ruleCache = new Map<string, CartRule[]>();

  /**
   * Convert metaobjects data to CartRule array
   */
  private static convertMetaobjectsToCartRules(metaobjects: any[]): CartRule[] {
    const rules: CartRule[] = [];

    metaobjects.forEach((metaobject) => {
      if (!metaobject.fields) return;

      const rule: Partial<CartRule> = {};

      metaobject.fields.forEach((field: any) => {
        const { key, value, references } = field;

        switch (key) {
          case 'rule_type':
            rule.rule_type = value;
            break;
          case 'min_quantity':
            rule.min_quantity = parseInt(value, 10);
            break;
          case 'max_quantity':
            rule.max_quantity = parseInt(value, 10);
            break;
          case 'when_variants':
            if (references && references.edges) {
              rule.when_variants = references.edges.map((edge: any) => edge.node.id);
            } else if (value) {
              rule.when_variants = value.split(',').map((id: string) => id.trim());
            }
            break;
          case 'then_variants':
            if (references && references.edges) {
              rule.then_variants = references.edges.map((edge: any) => edge.node.id);
            } else if (value) {
              rule.then_variants = value.split(',').map((id: string) => id.trim());
            }
            break;
          case 'group_id':
            rule.group_id = value;
            break;
          case 'bidirectional':
            rule.bidirectional = value === 'true' || value === true;
            break;
          case 'requires_any_of':
            if (references && references.edges) {
              rule.requires_any_of = references.edges.map((edge: any) => edge.node.id);
            } else if (value) {
              rule.requires_any_of = value.split(',').map((id: string) => id.trim());
            }
            break;
          case 'requires_none_of':
            if (references && references.edges) {
              rule.requires_none_of = references.edges.map((edge: any) => edge.node.id);
            } else if (value) {
              rule.requires_none_of = value.split(',').map((id: string) => id.trim());
            }
            break;
          default:
            break;
        }
      });

      if (rule.rule_type) {
        rules.push(rule as CartRule);
      }
    });

    return rules;
  }

  /**
   * Load all cart rules for a store from Shopify metaobjects or local data
   */
  async loadCartRules(storeCode: StoreCode): Promise<CartRule[]> {
    const cacheKey = storeCode;

    if (this.ruleCache.has(cacheKey)) {
      return this.ruleCache.get(cacheKey)!;
    }

    // Use local data for development
    if (USE_LOCAL_DATA) {
      const localRules = getCartRulesForStore(storeCode);
      this.ruleCache.set(cacheKey, localRules);
      return localRules;
    }

    try {
      const metaobjectType = 'cart_rule';

      const res = await shopifyFetch<any>({
        query: getCartRulesMetaobjects,
        shopifyDomain: SHOPIFY_STORE_DOMAIN[storeCode],
        accessToken: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
        variables: {
          type: metaobjectType,
          first: 100 // Adjust based on how many rules you expect
        }
      });

      const metaobjects = res.body.data?.metaobjects?.edges?.map((edge: any) => edge.node) || [];
      const cartRules = CartRules.convertMetaobjectsToCartRules(metaobjects);

      this.ruleCache.set(cacheKey, cartRules);
      return cartRules;
    } catch (error) {
      this.ruleCache.set(cacheKey, []);
      return [];
    }
  }

  /**
   * Get all cart rules that apply to the given variants
   */
  async getApplicableCartRules(
    storeCode: StoreCode,
    cartVariantIds: string[],
    customerTags?: string[]
  ): Promise<CartRule[]> {
    const allRules = await this.loadCartRules(storeCode);

    return allRules.filter((rule: CartRule) => {
      // Check customer tag conditions first
      if (rule.requires_customer_tags) {
        if (!customerTags || customerTags.length === 0) {
          return false; // Rule requires tags but customer has no tags (logged out)
        }
        const hasRequiredTag = rule.requires_customer_tags.some((tag) =>
          customerTags.includes(tag)
        );
        if (!hasRequiredTag) return false;
      }

      if (rule.requires_no_customer_tags) {
        if (!customerTags || customerTags.length === 0) {
          // Customer has no tags, so they don't have any of the excluded tags
          // Rule can apply
        } else {
          const hasExcludedTag = rule.requires_no_customer_tags.some((tag) =>
            customerTags.includes(tag)
          );
          if (hasExcludedTag) return false;
        }
      }

      // For bidirectional swap rules, check both when_variants and then_variants
      if (rule.rule_type === 'swap' && rule.bidirectional) {
        const matchesWhenVariants = rule.when_variants?.some((whenVariant: string) =>
          cartVariantIds.includes(whenVariant)
        );
        const matchesThenVariants = rule.then_variants?.some((thenVariant: string) =>
          cartVariantIds.includes(thenVariant)
        );
        return matchesWhenVariants || matchesThenVariants;
      }

      // For other rules, only check when_variants
      if (!rule.when_variants) return false;
      return rule.when_variants.some((whenVariant: string) => cartVariantIds.includes(whenVariant));
    });
  }

  /**
   * Get freebies for cart lines
   * Combines quantities across different cadences when determining freebie eligibility
   */
  async getFreebiesForCartLines(
    storeCode: StoreCode,
    cartLines: CartLineItem[],
    customerTags?: string[]
  ): Promise<CartLineItem[]> {
    const validLines = cartLines.filter((line) => line.merchandiseId && line.quantity);

    // Group cart lines by merchandise ID (ignoring cadence) and sum quantities
    const merchandiseQuantityMap = new Map<string, number>();
    validLines.forEach((line) => {
      const existing = merchandiseQuantityMap.get(line.merchandiseId!) || 0;
      merchandiseQuantityMap.set(line.merchandiseId!, existing + line.quantity!);
    });

    // Get unique merchandise IDs
    const uniqueMerchandiseIds = Array.from(merchandiseQuantityMap.keys());

    // Get all applicable cart rules for the cart variants
    const applicableRules = await this.getApplicableCartRules(
      storeCode,
      uniqueMerchandiseIds,
      customerTags
    );

    // Filter for auto_add rules and check quantity requirements
    const freebies: CartLineItem[] = [];

    applicableRules.forEach((rule: CartRule) => {
      if (rule.rule_type === 'auto_add' && rule.when_variants && rule.then_variants) {
        // Sum up quantities of all when_variants in the rule
        const totalQuantityForRule = rule.when_variants.reduce(
          (sum: number, whenVariant: string) => {
            const variantQuantity = merchandiseQuantityMap.get(whenVariant) || 0;
            return sum + variantQuantity;
          },
          0
        );

        // Check if the combined quantity meets the minimum requirement
        const hasMinQuantity =
          rule.min_quantity !== undefined && totalQuantityForRule >= rule.min_quantity;

        if (hasMinQuantity) {
          rule.then_variants.forEach((targetId: string) => {
            const isWelcomeKit =
              isOTPWelcomeKit(targetId, storeCode) ||
              isSubscriptionWelcomeKit(targetId, storeCode) ||
              isAgzFrotherWelcomeKit(targetId, storeCode);

            freebies.push({
              merchandiseId: targetId,
              quantity: 1,
              attributes: isWelcomeKit
                ? [
                    {
                      key: getWelcomeKitAttributeKey(targetId, storeCode),
                      value: 'true'
                    }
                  ]
                : undefined
            });
          });
        }
      }
    });

    return freebies;
  }

  /**
   * Expand bidirectional swap rules into explicit directional rules
   */
  private static expandBidirectionalRules(rules: CartRule[]): CartRule[] {
    const expandedRules: CartRule[] = [];

    rules.forEach((rule) => {
      if (
        rule.rule_type === 'swap' &&
        rule.bidirectional &&
        rule.when_variants &&
        rule.then_variants
      ) {
        // Add the original rule
        expandedRules.push({
          ...rule,
          bidirectional: false // Mark as non-bidirectional to avoid infinite expansion
        });

        // Create reverse rule
        const reverseRule: CartRule = {
          ...rule,
          when_variants: [...rule.then_variants],
          then_variants: [...rule.when_variants],
          bidirectional: false
        };

        // Handle conditional requirements for bidirectional rules
        if (rule.requires_any_of) {
          // Reverse: swap back when NONE of the required variants are present
          reverseRule.requires_any_of = undefined;
          reverseRule.requires_none_of = [...rule.requires_any_of];
        } else if (rule.requires_none_of) {
          // Reverse: swap back when ANY of the excluded variants are present
          reverseRule.requires_none_of = undefined;
          reverseRule.requires_any_of = [...rule.requires_none_of];
        } else {
          // Original quantity-based bidirectional logic
          // For reverse direction, swap when total quantity is less than the original min_quantity
          reverseRule.min_quantity = undefined;
          reverseRule.max_quantity = (rule.min_quantity || 2) - 1; // e.g., if min was 2, max becomes 1
        }

        expandedRules.push(reverseRule);
      } else {
        // Add non-bidirectional rules as-is
        expandedRules.push(rule);
      }
    });

    return expandedRules;
  }

  /**
   * Get swaps for cart lines
   */
  async getSwapsForCartLines(
    storeCode: StoreCode,
    cartLines: CartLineItem[],
    customerTags?: string[]
  ): Promise<{ originalId: string; swapToId: string; quantity: number }[]> {
    const validLines = cartLines.filter((line) => line.merchandiseId && line.quantity);

    // Group cart lines by merchandise ID and sum quantities
    const merchandiseQuantityMap = new Map<string, number>();
    validLines.forEach((line) => {
      const existing = merchandiseQuantityMap.get(line.merchandiseId!) || 0;
      merchandiseQuantityMap.set(line.merchandiseId!, existing + line.quantity!);
    });

    // Get unique merchandise IDs
    const uniqueMerchandiseIds = Array.from(merchandiseQuantityMap.keys());

    // Get all applicable cart rules for the cart variants
    const applicableRules = await this.getApplicableCartRules(
      storeCode,
      uniqueMerchandiseIds,
      customerTags
    );

    // Expand bidirectional rules
    const expandedRules = CartRules.expandBidirectionalRules(applicableRules);

    const swaps: { originalId: string; swapToId: string; quantity: number }[] = [];

    // Group swap rules by group_id
    const rulesByGroup = new Map<string, CartRule[]>();
    const rulesWithoutGroup: CartRule[] = [];

    expandedRules
      .filter((rule) => rule.rule_type === 'swap')
      .forEach((rule) => {
        if (rule.group_id) {
          if (!rulesByGroup.has(rule.group_id)) {
            rulesByGroup.set(rule.group_id, []);
          }
          rulesByGroup.get(rule.group_id)!.push(rule);
        } else {
          rulesWithoutGroup.push(rule);
        }
      });

    // Process grouped rules (combine quantities within group)
    rulesByGroup.forEach((groupRules) => {
      // Calculate total quantity for all variants in this group
      const groupVariants = new Set<string>();
      groupRules.forEach((rule) => {
        rule.when_variants?.forEach((variant) => groupVariants.add(variant));
      });

      const totalGroupQuantity = Array.from(groupVariants).reduce((sum, variant) => {
        return sum + (merchandiseQuantityMap.get(variant) || 0);
      }, 0);

      // Find applicable rules for this group based on total quantity
      const applicableGroupRules = groupRules
        .filter((rule) => {
          if (!rule.when_variants || !rule.then_variants) return false;

          // Check if any variant in this rule is in the cart
          const hasVariantInCart = rule.when_variants.some((variant) =>
            merchandiseQuantityMap.has(variant)
          );
          if (!hasVariantInCart) return false;

          // Check quantity constraints against total group quantity
          if (rule.min_quantity !== undefined && totalGroupQuantity < rule.min_quantity) {
            return false;
          }

          if (rule.max_quantity !== undefined && totalGroupQuantity > rule.max_quantity) {
            return false;
          }

          // Check conditional requirements based on other products in cart
          if (rule.requires_any_of) {
            const hasRequiredProduct = rule.requires_any_of.some((requiredVariant) =>
              merchandiseQuantityMap.has(requiredVariant)
            );
            if (!hasRequiredProduct) return false;
          }

          if (rule.requires_none_of) {
            const hasExcludedProduct = rule.requires_none_of.some((excludedVariant) =>
              merchandiseQuantityMap.has(excludedVariant)
            );
            if (hasExcludedProduct) return false;
          }

          return true;
        })
        .sort((a, b) => (b.min_quantity || 0) - (a.min_quantity || 0));

      // Apply swaps for variants in this group
      if (applicableGroupRules.length > 0) {
        // Apply ALL applicable rules in the group, not just the first one
        applicableGroupRules.forEach((swapRule) => {
          // Apply swaps only to variants that match the rule's when_variants and are in the cart
          if (
            swapRule.when_variants &&
            swapRule.then_variants &&
            swapRule.then_variants.length > 0
          ) {
            swapRule.when_variants.forEach((variant) => {
              const quantity = merchandiseQuantityMap.get(variant);
              if (quantity && quantity > 0) {
                swaps.push({
                  originalId: variant,
                  swapToId: swapRule.then_variants![0],
                  quantity
                });
              }
            });
          }
        });
      }
    });

    // Process rules without group_id (original individual logic)
    validLines.forEach((line) => {
      const merchandiseId = line.merchandiseId!;
      const quantity = line.quantity!;

      // Find swap rules that apply to this merchandise ID (without group_id)
      const applicableSwapRules = rulesWithoutGroup
        .filter((rule) => {
          if (!rule.when_variants || !rule.then_variants) return false;

          // Check if this merchandise ID is in the when_variants
          if (!rule.when_variants.includes(merchandiseId)) return false;

          // Check quantity constraints
          if (rule.min_quantity !== undefined && quantity < rule.min_quantity) {
            return false;
          }

          if (rule.max_quantity !== undefined && quantity > rule.max_quantity) {
            return false;
          }

          // Check conditional requirements based on other products in cart
          if (rule.requires_any_of) {
            const hasRequiredProduct = rule.requires_any_of.some((requiredVariant) =>
              merchandiseQuantityMap.has(requiredVariant)
            );
            if (!hasRequiredProduct) return false;
          }

          if (rule.requires_none_of) {
            const hasExcludedProduct = rule.requires_none_of.some((excludedVariant) =>
              merchandiseQuantityMap.has(excludedVariant)
            );
            if (hasExcludedProduct) return false;
          }

          return true;
        })
        .sort((a, b) => (b.min_quantity || 0) - (a.min_quantity || 0));

      if (applicableSwapRules.length > 0) {
        const swapRule = applicableSwapRules[0];
        if (swapRule.then_variants && swapRule.then_variants.length > 0) {
          swaps.push({
            originalId: merchandiseId,
            swapToId: swapRule.then_variants[0],
            quantity
          });
        }
      }
    });

    return swaps;
  }

  /**
   * Consolidate cart lines by merchandise ID and selling plan ID (cadence)
   * This keeps different cadences as separate line items while consolidating identical items
   */
  static consolidateCartLines(lines: CartLineItem[]): CartLineItem[] {
    const consolidatedMap = new Map<string, CartLineItem>();

    lines.forEach((line) => {
      if (!line.merchandiseId) return;

      // Create a unique key that considers both merchandiseId and sellingPlanId
      // This ensures different cadences are treated as separate line items
      const key = `${line.merchandiseId}:${line.sellingPlanId || 'one-time'}`;

      const existing = consolidatedMap.get(key);
      if (existing) {
        consolidatedMap.set(key, {
          ...existing,
          quantity: (existing.quantity || 0) + (line.quantity || 0),
          // Preserve sellingPlanId and attributes from the line with a sellingPlanId
          // This ensures subscription status is maintained during consolidation
          sellingPlanId: existing.sellingPlanId || line.sellingPlanId,
          attributes:
            existing.attributes && existing.attributes.length > 0
              ? existing.attributes
              : line.attributes
        });
      } else {
        consolidatedMap.set(key, { ...line });
      }
    });

    return Array.from(consolidatedMap.values()).filter((line) => (line.quantity || 0) > 0);
  }

  /**
   * Apply swaps to cart lines
   */
  static applySwapsToCartLines(
    originalLines: CartLineItem[],
    swaps: { originalId: string; swapToId: string; quantity: number }[]
  ): CartLineItem[] {
    if (swaps.length === 0) return originalLines;

    return originalLines.map((line) => {
      const swap = swaps.find((s) => s.originalId === line.merchandiseId);
      if (swap) {
        return {
          ...line,
          merchandiseId: swap.swapToId,
          quantity: swap.quantity,
          // Preserve sellingPlanId and attributes during swaps
          // This ensures subscription status is maintained when products are swapped
          sellingPlanId: line.sellingPlanId,
          attributes: line.attributes
        };
      }
      return line;
    });
  }

  /**
   * Validate cart line item for required properties
   * Ensures the line has a merchandiseId and a valid quantity greater than 0
   */
  static isValidCartLine(line: any): boolean {
    return !!line.merchandiseId && !!line.quantity && line.quantity > 0;
  }

  /**
   * Process cart rules for new cart creation or adding items
   */
  async processCartRules(
    storeCode: StoreCode,
    cartLines: CartLineItem[],
    customerTags?: string[],
    getMaxQuantity?: (merchandiseId: string, isSubscription: boolean) => number
  ): Promise<{
    processedLines: CartLineItem[];
    quantityErrors: Array<{
      merchandiseId: string;
      requestedQuantity: number;
      cappedQuantity: number;
      errorMessage: string;
    }>;
  }> {
    // Step 1: Consolidate duplicates
    let processedLines = CartRules.consolidateCartLines(cartLines);

    // Step 2: Apply swaps
    try {
      const swaps = await this.getSwapsForCartLines(storeCode, processedLines, customerTags);
      if (swaps.length > 0) {
        processedLines = CartRules.applySwapsToCartLines(processedLines, swaps);
      }
    } catch (error) {
      // Continue without swaps if there's an error
    }

    // Step 3: Apply freebies
    let freebies: CartLineItem[] = [];
    try {
      freebies = await this.getFreebiesForCartLines(storeCode, processedLines, customerTags);
    } catch (error) {
      // Continue without freebies if there's an error
    }

    // Step 4: Combine and consolidate
    const allLines = [...processedLines, ...freebies];
    const consolidatedLines = CartRules.consolidateCartLines(allLines);

    // Step 5: Apply quantity caps
    const { cappedLines, quantityErrors } = CartRules.applyQuantityCaps(
      consolidatedLines,
      getMaxQuantity
    );

    return { processedLines: cappedLines, quantityErrors };
  }

  /**
   * Identify freebies vs purchased items
   */
  async separatePurchasedFromFreebies(
    storeCode: StoreCode,
    cartLines: CartLineItem[],
    customerTags?: string[]
  ): Promise<{
    purchasedItems: CartLineItem[];
    freebieItems: CartLineItem[];
  }> {
    const allFreebieIds = new Set<string>();

    // Get unique merchandise IDs
    const uniqueMerchandiseIds = Array.from(
      new Set(cartLines.map((line) => line.merchandiseId).filter(Boolean))
    ) as string[];

    // Get all applicable cart rules for the cart variants
    const applicableRules = await this.getApplicableCartRules(
      storeCode,
      uniqueMerchandiseIds,
      customerTags
    );

    // Collect all freebie target IDs
    applicableRules.forEach((rule: CartRule) => {
      if (rule.rule_type === 'auto_add' && rule.then_variants) {
        rule.then_variants.forEach((targetId: string) => allFreebieIds.add(targetId));
      }
    });

    const purchasedItems: CartLineItem[] = [];
    const freebieItems: CartLineItem[] = [];

    cartLines.forEach((line) => {
      if (line.merchandiseId && allFreebieIds.has(line.merchandiseId)) {
        freebieItems.push(line);
      } else {
        purchasedItems.push(line);
      }
    });

    return { purchasedItems, freebieItems };
  }

  /**
   * Process cart rules for cart updates (client-side version)
   */
  async processCartUpdatesWithRules(
    storeCode: StoreCode,
    updateLines: CartLineItem[],
    currentCartLines: CartLineItem[],
    customerTags?: string[]
  ): Promise<{
    shouldClearCart: boolean;
    itemsToAdd: CartLineItem[];
    quantityErrors: Array<{
      merchandiseId: string;
      requestedQuantity: number;
      cappedQuantity: number;
      errorMessage: string;
    }>;
  }> {
    // Separate purchased items from freebies
    const { purchasedItems } = await this.separatePurchasedFromFreebies(
      storeCode,
      currentCartLines,
      customerTags
    );

    // Create a map of existing cart line properties by merchandiseId and sellingPlanId to preserve subscription properties
    const existingLinePropsMap = new Map<string, { sellingPlanId?: string; attributes?: any[] }>();

    // Build a map from current cart lines to preserve subscription properties
    currentCartLines.forEach((line) => {
      if (line.merchandiseId) {
        // Use the same key format as consolidateCartLines for consistency
        const key = `${line.merchandiseId}:${line.sellingPlanId || 'one-time'}`;
        existingLinePropsMap.set(key, {
          sellingPlanId: line.sellingPlanId,
          attributes: line.attributes
        });
      }
    });

    // Process update lines, preserving subscription properties
    // Include quantity 0 items so they can properly remove existing items
    const newCartLines: CartLineItem[] = updateLines
      .filter((line) => (line.quantity || 0) >= 0)
      .map((line) => {
        // If update line has explicit subscription properties, use them
        if (line.sellingPlanId || line.attributes) {
          return {
            merchandiseId: line.merchandiseId!,
            quantity: line.quantity || 0,
            sellingPlanId: line.sellingPlanId,
            attributes: line.attributes
          };
        }

        // Otherwise, preserve existing subscription properties for simple quantity updates
        let existingProps: { sellingPlanId?: string; attributes?: any[] } | undefined;

        // First check for one-time purchase
        const oneTimeKey = `${line.merchandiseId}:one-time`;
        existingProps = existingLinePropsMap.get(oneTimeKey);

        // If not found, check for any subscription variant
        if (!existingProps) {
          const keys = Array.from(existingLinePropsMap.keys());
          const matchingKey = keys.find((key) => key.startsWith(`${line.merchandiseId}:`));
          if (matchingKey) {
            existingProps = existingLinePropsMap.get(matchingKey);
          }
        }

        return {
          merchandiseId: line.merchandiseId!,
          quantity: line.quantity || 0,
          // Preserve sellingPlanId and attributes from existing cart line
          ...(existingProps?.sellingPlanId && { sellingPlanId: existingProps.sellingPlanId }),
          ...(existingProps?.attributes && { attributes: existingProps.attributes })
        };
      });

    // For cart updates, the updateLines represent final desired quantities, not additions
    // We need to replace existing items with update lines, keeping items that aren't being updated
    const updateLinesMap = new Map<string, CartLineItem>();
    newCartLines.forEach((line) => {
      if (line.merchandiseId) {
        // Create a unique key that considers both merchandiseId and sellingPlanId
        const key = `${line.merchandiseId}:${line.sellingPlanId || 'one-time'}`;
        updateLinesMap.set(key, line);
      }
    });

    // Start with items that aren't being updated
    const finalPurchasedLines: CartLineItem[] = [];

    // Add existing items that aren't being updated
    purchasedItems.forEach((line) => {
      if (line.merchandiseId) {
        const key = `${line.merchandiseId}:${line.sellingPlanId || 'one-time'}`;
        if (!updateLinesMap.has(key)) {
          finalPurchasedLines.push(line);
        }
      }
    });

    // Add all the update lines (these replace any existing items with same key)
    // Filter out quantity 0 items - they were used to remove existing items
    finalPurchasedLines.push(...newCartLines.filter((line) => (line.quantity || 0) > 0));

    // Apply cart rules to the final purchased lines
    const { processedLines, quantityErrors } = await this.processCartRules(
      storeCode,
      finalPurchasedLines,
      customerTags
    );

    return {
      shouldClearCart: true,
      itemsToAdd: processedLines.filter(
        (line: CartLineItem) => line.merchandiseId && line.quantity && line.quantity > 0
      ),
      quantityErrors
    };
  }

  /**
   * Apply quantity caps to cart lines based on product metafields
   * Returns both the capped lines and any error messages for quantities that were reduced
   */
  static applyQuantityCaps(
    lines: CartLineItem[],
    getMaxQuantity?: (merchandiseId: string, isSubscription: boolean) => number
  ): {
    cappedLines: CartLineItem[];
    quantityErrors: Array<{
      merchandiseId: string;
      requestedQuantity: number;
      cappedQuantity: number;
      errorMessage: string;
    }>;
  } {
    const cappedLines: CartLineItem[] = [];
    const quantityErrors: Array<{
      merchandiseId: string;
      requestedQuantity: number;
      cappedQuantity: number;
      errorMessage: string;
    }> = [];

    lines.forEach((line) => {
      if (!line.merchandiseId || !line.quantity) {
        cappedLines.push(line);
        return;
      }

      // Default max quantity logic - can be overridden by getMaxQuantity function
      let maxQuantity: number;
      if (getMaxQuantity) {
        // Check if this is a subscription item (simplified check)
        const isSubscription = !!line.sellingPlanId;
        maxQuantity = getMaxQuantity(line.merchandiseId, isSubscription);
      } else {
        // Default fallback logic
        const isSubscription = !!line.sellingPlanId;
        maxQuantity = isSubscription
          ? DEFAULT_MAX_QUANTITY_SUBSCRIPTION
          : DEFAULT_MAX_QUANTITY_ONE_TIME;
      }

      if (line.quantity > maxQuantity) {
        // Cap the quantity and record the error
        quantityErrors.push({
          merchandiseId: line.merchandiseId,
          requestedQuantity: line.quantity,
          cappedQuantity: maxQuantity,
          errorMessage: `Maximum QTY of ${maxQuantity} reached.`
        });

        cappedLines.push({
          ...line,
          quantity: maxQuantity
        });
      } else {
        cappedLines.push(line);
      }
    });

    return { cappedLines, quantityErrors };
  }

  /**
   * Check if a customer qualifies for a discounted variant swap
   * Used for PDP pricing display
   */
  async getDiscountedVariantId(
    storeCode: StoreCode,
    originalVariantId: string,
    customerTags?: string[]
  ): Promise<string | null> {
    try {
      const allRules = await this.loadCartRules(storeCode);

      // Find swap rules that apply to this variant and have customer tag requirements
      const applicableSwapRules = allRules.filter((rule: CartRule) => {
        if (rule.rule_type !== 'swap') return false;
        if (!rule.when_variants?.includes(originalVariantId)) return false;
        if (!rule.requires_customer_tags) return false;

        // Check customer tag requirements
        if (!customerTags || customerTags.length === 0) {
          return false; // No customer tags, can't qualify for discount
        }

        const hasRequiredTag = rule.requires_customer_tags.some((tag) =>
          customerTags.includes(tag)
        );
        return hasRequiredTag;
      });

      // Return the first applicable discounted variant
      if (applicableSwapRules.length > 0 && applicableSwapRules[0].then_variants) {
        return applicableSwapRules[0].then_variants[0];
      }

      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error getting discounted variant:', error);
      return null;
    }
  }

  /**
   * Check if a customer qualifies for any cart rule benefits on a specific variant
   * Used for PDP to show potential savings or freebies
   */
  async getVariantCartRuleBenefits(
    storeCode: StoreCode,
    variantId: string,
    customerTags?: string[]
  ): Promise<{
    discountedVariantId?: string;
    freebieVariantIds?: string[];
  }> {
    const benefits: {
      discountedVariantId?: string;
      freebieVariantIds?: string[];
    } = {};

    try {
      // Check for discount eligibility
      const discountedVariantId = await this.getDiscountedVariantId(
        storeCode,
        variantId,
        customerTags
      );
      if (discountedVariantId) {
        benefits.discountedVariantId = discountedVariantId;
      }

      // Check for freebie eligibility
      const freebieIds = await this.getAutoAddItemsForDebug(storeCode, variantId, 1);
      if (freebieIds.length > 0) {
        benefits.freebieVariantIds = freebieIds;
      }

      return benefits;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error getting variant cart rule benefits:', error);
      return {};
    }
  }

  /**
   * Get auto_add items for debug highlighting
   * Returns merchandise IDs that would be automatically added as freebies
   */
  async getAutoAddItemsForDebug(
    storeCode: StoreCode,
    merchandiseId: string,
    quantity: number = 1
  ): Promise<string[]> {
    const autoAddIds = new Set<string>();

    try {
      const allRules = await this.loadCartRules(storeCode);

      // Find auto_add rules where this merchandise ID is a trigger
      const autoAddRules = allRules.filter((rule: CartRule) => {
        return rule.rule_type === 'auto_add' && rule.when_variants?.includes(merchandiseId);
      });

      autoAddRules.forEach((rule: CartRule) => {
        if (rule.then_variants) {
          // Check if quantity meets minimum requirement
          const hasMinQuantity = rule.min_quantity === undefined || quantity >= rule.min_quantity;
          if (hasMinQuantity) {
            rule.then_variants.forEach((id) => autoAddIds.add(id));
          }
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error getting auto-add items for debug:', error);
    }

    return Array.from(autoAddIds);
  }
}
